function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef } from 'react';
import { node, object, oneOf, string } from 'prop-types';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import SovosTooltip from '../sovos-tooltip';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
const StyledAvatar = styled(Avatar, {
  shouldForwardProp: prop => prop !== 'backgroundColor' && prop !== 'color'
})(_ref => {
  let {
    backgroundColor: bgColorProp,
    color: colorProp,
    size: sizeProp,
    theme: {
      palette,
      spacing,
      typography
    }
  } = _ref;
  const size = (() => {
    const sizeKey = sizeProp === null || sizeProp === void 0 ? void 0 : sizeProp.toLowerCase();
    if (sizeKey === 'small') return spacing(3);
    if (sizeKey === 'large') return spacing(5);
    return spacing(4);
  })();
  return {
    ...typography.body2,
    backgroundColor: bgColorProp !== null && bgColorProp !== void 0 ? bgColorProp : palette.grey[300],
    color: colorProp !== null && colorProp !== void 0 ? colorProp : palette.text.secondary,
    display: 'inline-flex',
    height: size,
    width: size
  };
});
const SovosAvatar = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
    backgroundColor,
    children,
    classes,
    className,
    color,
    name,
    noNameMessage,
    size = 'medium',
    style = {},
    surname,
    ...rest
  } = _ref2;
  const {
    t
  } = useMosaicTranslation();
  const renderToolTipTitle = () => {
    if (name && surname) {
      return "".concat(name, " ").concat(surname);
    }
    if (name && !surname) {
      return "".concat(name);
    }
    if (!name && surname) {
      return "".concat(surname);
    }
    return noNameMessage || t('avatar.noName');
  };
  const renderInitials = () => {
    if (children) return children;
    if (name && surname) {
      return name[0].toUpperCase() + surname[0].toUpperCase();
    }
    if (name && !surname) {
      return name[0].toUpperCase();
    }
    if (!name && surname) {
      return surname[0].toUpperCase();
    }
    return '?';
  };
  return /*#__PURE__*/React.createElement(SovosTooltip, {
    title: renderToolTipTitle()
  }, /*#__PURE__*/React.createElement(StyledAvatar, _extends({
    backgroundColor: backgroundColor,
    classes: classes,
    className: clsx('sovosAvatar', className),
    color: color,
    ref: ref,
    size: size,
    style: style
  }, rest), renderInitials()));
});
SovosAvatar.propTypes = {
  /**
   * Override background color
   */
  backgroundColor: string,
  /**
   * Override children. Defaults to first and last initial
   */
  children: node,
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * Override the text color
   */
  color: string,
  /**
   * First name to display
   */
  name: string,
  /**
   * Tooltip text to display when `name` and `surname` are empty
   */
  noNameMessage: string,
  /**
   * Size of Avatar
   */
  size: oneOf(['small', 'medium', 'large']),
  /**
   * Inline styles applied to the root element
   */
  style: object,
  /**
   * Surname to display
   */
  surname: string
};
SovosAvatar.baseComponent = {
  name: 'Avatar',
  link: 'avatar/'
};
export default SovosAvatar;